// Archivo: src/pages/SectionPage.js

import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import "../components/RichTextContent.css";
import {
  getPublicServiceSections,
  getSectionDetailsById,
  getSubsectionsBySectionId,
} from "../services/service.service";
import SubSectionDisplay from "../components/SubSectionDisplay";
import SectionDisplay from "../components/SectionDisplay"; // NUEVO IMPORT

// --- Componente Skeleton Loader Mejorado ---
const SkeletonLoader = () => (
  <div className="animate-pulse space-y-12">
    <div className="h-6 w-1/4 bg-gray-200 rounded-md"></div>
    <div className="space-y-4 text-center">
      <div className="h-12 w-3/4 mx-auto bg-gray-300 rounded-md"></div>
      <div className="h-6 w-full max-w-3xl mx-auto bg-gray-200 rounded-md"></div>
      <div className="h-6 w-2/3 mx-auto bg-gray-200 rounded-md"></div>
    </div>
    <div className="border-t border-gray-200 pt-12">
      <div className="h-48 bg-gray-200 rounded-lg"></div>
    </div>
  </div>
);

// --- Componente de Navegación Rediseñado ---
const NavigationButton = ({ to, direction, title }) => {
  const isPrev = direction === "prev";
  const content = (
    <>
      <div
        className={`p-3 bg-gray-100 rounded-full group-hover:bg-gob-azul-medio transition-colors duration-300 ${isPrev ? "order-1" : "order-2"}`}
      >
        <svg
          className={`w-6 h-6 text-gob-azul-medio group-hover:text-white`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d={isPrev ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"}
          />
        </svg>
      </div>
      <div
        className={`flex flex-col ${isPrev ? "order-2 text-left" : "order-1 text-right"}`}
      >
        <span className="text-xs font-semibold text-gob-azul-medio uppercase tracking-wider">
          {isPrev ? "Sección Anterior" : "Siguiente Sección"}
        </span>
        <span className="text-lg font-bold text-gob-azul-oscuro group-hover:text-gob-azul-medio transition-colors">
          {title}
        </span>
      </div>
    </>
  );

  return (
    <Link to={to} className="group w-full md:w-auto">
      <div
        className={`flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-lg hover:border-gob-azul-medio transform hover:-translate-y-1 transition-all duration-300 ${isPrev ? "justify-start" : "justify-end"}`}
      >
        {content}
      </div>
    </Link>
  );
};

export default function SectionPage() {
  const { serviceId, sectionId } = useParams();

  const [sectionDetails, setSectionDetails] = useState(null);
  const [subsections, setSubsections] = useState([]);
  const [allSections, setAllSections] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchSectionData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [detailsData, allSectionsData, subsectionsData] =
          await Promise.all([
            getSectionDetailsById(sectionId),
            getPublicServiceSections(serviceId),
            getSubsectionsBySectionId(sectionId),
          ]);

        console.log("Section details loaded:", detailsData); // Debug log
        console.log("Videos found:", detailsData?.videos?.length || 0); // Debug log
        console.log("Documents found:", detailsData?.documents?.length || 0); // Debug log

        setSectionDetails(detailsData);
        setAllSections(allSectionsData);
        setSubsections(subsectionsData);

        const currentIdx = allSectionsData.findIndex(
          (sec) => sec.id === parseInt(sectionId),
        );
        setCurrentIndex(currentIdx);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSectionData();
  }, [serviceId, sectionId]);

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (error) {
    return (
      <div className="text-center p-10 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-2xl font-bold text-red-700 mb-2">
          Ocurrió un error
        </h3>
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  const prevSection = currentIndex > 0 ? allSections[currentIndex - 1] : null;
  const nextSection =
    currentIndex < allSections.length - 1
      ? allSections[currentIndex + 1]
      : null;

  return (
    <div className="animate-fade-in-up">
      {/* --- BOTÓN "VOLVER AL SUMARIO" REDISEÑADO --- */}
      <div className="mb-8">
        <Link
          to={`/servicios/${serviceId}`}
          className="group inline-flex items-center gap-3 py-2 px-5 bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-lg hover:border-gob-azul-medio transition-all duration-300 transform hover:-translate-y-0.5"
        >
          <svg
            className="w-5 h-5 text-gob-azul-medio transition-transform group-hover:-translate-x-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          <span className="font-bold text-gob-azul-oscuro group-hover:text-gob-azul-medio transition-colors">
            Volver al Sumario
          </span>
        </Link>
      </div>

      <header className="text-center py-8 mb-12 border-b-2 border-gob-azul-medio border-opacity-20">
        <h1 className="text-5xl font-extrabold text-gob-azul-oscuro mb-4 tracking-tight">
          {sectionDetails?.details.title}
          {}
        </h1>
        <div
          className="text-lg text-gray-500 text-left mx-auto max-w-3xl rich-text-content"
          dangerouslySetInnerHTML={{
            __html: sectionDetails?.details.description || "",
          }}
        />
      </header>

      <main className="space-y-16">
        {/* NUEVO: Mostrar contenido de la sección principal (videos y documentos) */}
        <SectionDisplay sectionDetails={sectionDetails} />

        {/* Separador visual si hay contenido de sección Y subsecciones */}
        {sectionDetails &&
          (sectionDetails.videos?.length > 0 ||
            sectionDetails.documents?.length > 0) &&
          subsections.length > 0 && (
            <div className="border-t border-gray-200 pt-16">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-800 inline-block relative pb-2 after:content-[''] after:absolute after:left-1/2 after:-translate-x-1/2 after:bottom-0 after:w-20 after:h-1 after:bg-gob-azul-medio after:rounded-full">
                  {/* CONTENIDO ADICIONAL */}
                </h2>
              </div>
            </div>
          )}

        {/* Subsecciones existentes */}
        {subsections.length > 0 ? (
          subsections.map((sub) => (
            <SubSectionDisplay key={sub.id} subsection={sub} />
          ))
        ) : // Solo mostrar este mensaje si tampoco hay contenido principal
        !sectionDetails ||
          ((!sectionDetails.videos || sectionDetails.videos.length === 0) &&
            (!sectionDetails.documents ||
              sectionDetails.documents.length === 0)) ? (
          <div className="text-center text-gray-500 py-16 bg-gray-50 rounded-lg">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">
              Sin Contenidos
            </h3>
            <p className="mt-1 text-base text-gray-500">
              Actualmente no hay contenidos disponibles para esta sección.
            </p>
          </div>
        ) : null}
      </main>

      {/* Navegación Inferior Rediseñada */}
      <footer className="flex flex-col md:flex-row justify-between items-center gap-6 mt-16 py-8 border-t">
        <div className="w-full md:w-auto flex justify-start">
          {prevSection ? (
            <NavigationButton
              to={`/servicios/${serviceId}/secciones/${prevSection.id}`}
              direction="prev"
              title={prevSection.title}
            />
          ) : (
            <div />
          )}
        </div>
        <div className="w-full md:w-auto flex justify-end">
          {nextSection && (
            <NavigationButton
              to={`/servicios/${serviceId}/secciones/${nextSection.id}`}
              direction="next"
              title={nextSection.title}
            />
          )}
        </div>
      </footer>
    </div>
  );
}
